/**
 * Pre-authored macros — committed fixtures.
 *
 * DESIGN TIME (here, now, via Claude Code): a strong model decided which
 * primitive operations to call, in what order, to accomplish each GitHub
 * maintainer task. We capture that decision as a representative *trajectory*
 * (the ordered primitive calls + example results) and commit it.
 *
 * RUNTIME (the browser): value-tracing generalizes each committed trajectory
 * into a parameterized macro (see distiller.ts), which a small in-browser
 * model routes to. No strong model, no key, no network beyond GitHub public
 * reads runs in the browser.
 *
 * The UI shows each macro's trajectory verbatim as the "how this was made"
 * provenance — the design-time/runtime split, stated transparently.
 *
 * The example results below are representative captures from authoring time;
 * at runtime the macro re-runs the primitives against the live repo, so the
 * data the user sees is always fresh. The trajectory is provenance, not a
 * cache.
 */
import type { TrajectoryStep } from "./distiller.js";

export interface AuthoredMacroSpec {
  name: string;
  intent: string;
  /** Task-level parameters present at authoring time (become macro params). */
  taskParams: Record<string, unknown>;
  /** The recorded design-time trajectory (provenance + distillation input). */
  trajectory: TrajectoryStep[];
  /** One line shown in the UI describing how/when this was authored. */
  authoredNote: string;
}

export const AUTHORED_MACROS: AuthoredMacroSpec[] = [
  {
    name: "triage_newest_pull",
    intent:
      "Triage the newest open pull request in a repository: list open PRs, " +
      "inspect the newest one and the files it changes, then propose review " +
      "labels (dry-run). Use for requests like 'triage the latest PR'.",
    taskParams: { owner: "octocat", repo: "hello-world" },
    authoredNote: "Authored design-time by Claude Code — 4-step trajectory, value-traced.",
    trajectory: [
      {
        tool: "gh_list_pulls",
        args: { owner: "octocat", repo: "hello-world", state: "open" },
        result: [
          { number: 42, title: "Add retry/backoff to uploader", user: "alice", createdAt: "2026-05-24T10:00:00Z", draft: false },
          { number: 39, title: "Fix docs typo", user: "bob", createdAt: "2026-05-23T08:00:00Z", draft: false },
        ],
      },
      {
        tool: "gh_get_pull",
        args: { owner: "octocat", repo: "hello-world", number: 42 },
        result: {
          number: 42, title: "Add retry/backoff to uploader", body: "Adds exponential backoff on 5xx.",
          user: "alice", createdAt: "2026-05-24T10:00:00Z", draft: false,
          additions: 120, deletions: 8, changedFiles: 3, baseRef: "main", headRef: "retry",
        },
      },
      {
        tool: "gh_list_pull_files",
        args: { owner: "octocat", repo: "hello-world", number: 42 },
        result: [
          { filename: "src/uploader.ts", status: "modified", additions: 100, deletions: 6 },
          { filename: "test/uploader.test.ts", status: "added", additions: 20, deletions: 2 },
        ],
      },
      {
        tool: "gh_suggest_labels_dryrun",
        args: { owner: "octocat", repo: "hello-world", number: 42, labels: ["needs-review", "enhancement"] },
        result: { dryRun: true, target: "octocat/hello-world#42", wouldApply: ["needs-review", "enhancement"], note: "dry-run — no write performed" },
      },
    ],
  },
  {
    name: "summarize_open_issues",
    intent:
      "Summarize the open issues in a repository: list current open issues " +
      "with their labels and comment counts. Use for requests like 'what " +
      "issues are open' or 'summarize the open issues'.",
    taskParams: { owner: "octocat", repo: "hello-world" },
    authoredNote: "Authored design-time by Claude Code — read-only, single step.",
    trajectory: [
      {
        tool: "gh_list_issues",
        args: { owner: "octocat", repo: "hello-world", state: "open" },
        result: [
          { number: 51, title: "Flaky upload test", labels: ["bug"], comments: 4, createdAt: "2026-05-20T00:00:00Z" },
          { number: 47, title: "Document the retry policy", labels: ["documentation"], comments: 1, createdAt: "2026-05-18T00:00:00Z" },
        ],
      },
    ],
  },
  {
    name: "repo_health",
    intent:
      "Report a repository's headline health: stars, open-issue count, and " +
      "default branch. Use for requests like 'how healthy is this repo' or " +
      "'give me the repo overview'.",
    taskParams: { owner: "octocat", repo: "hello-world" },
    authoredNote: "Authored design-time by Claude Code — read-only, single step.",
    trajectory: [
      {
        tool: "gh_get_repo",
        args: { owner: "octocat", repo: "hello-world" },
        result: { fullName: "octocat/hello-world", description: "My first repo", stars: 1820, openIssues: 12, defaultBranch: "main" },
      },
    ],
  },
];
